// =============================================================================
// siteMapThumb — a satellite thumbnail for a map card, no Leaflet needed
// (mig 390, 26 Sep 2026)
// =============================================================================
// The list screen and the project/BID cards show a real satellite tile of
// the site's centre so you recognise a map by the ground, not the number.
// One <img> per card, straight from the tile server; the same URL constant
// the editor uses, so Nearmap swaps in here at the same time.
// =============================================================================

export const SATELLITE_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

// Web-Mercator tile containing a lat/lng at a zoom (slippy-map maths).
export function tileFor(lat: number, lng: number, z: number): { x: number; y: number; z: number } {
  const n = Math.pow(2, z);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latR = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n);
  return { x: Math.max(0, Math.min(n - 1, x)), y: Math.max(0, Math.min(n - 1, y)), z };
}

export function thumbUrl(lat: number | null | undefined, lng: number | null | undefined, z = 16): string | null {
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return null;
  const t = tileFor(Number(lat), Number(lng), z);
  return SATELLITE_TILE_URL.replace("{z}", String(t.z)).replace("{y}", String(t.y)).replace("{x}", String(t.x));
}

// ---------------------------------------------------------------------------
// How close does the imagery actually go here?
// Esri serves the same grey "Map data not yet available" PNG for any tile it
// hasn't got, with a 200, so Leaflet never sees an error. We fetch the centre
// tile at a zoom nothing has (23) to learn what that placeholder looks like,
// then step down from 19 until a tile differs — that is the closest real
// imagery at this site. Six small requests, a second or so. Any failure
// (no CORS, offline) resolves null and the map carries on as it was.
// ---------------------------------------------------------------------------
async function tileBytes(lat: number, lng: number, z: number): Promise<number | null> {
  try {
    const t = tileFor(lat, lng, z);
    const url = SATELLITE_TILE_URL.replace("{z}", String(t.z)).replace("{y}", String(t.y)).replace("{x}", String(t.x));
    const r = await fetch(url, { mode: "cors", cache: "force-cache" });
    if (!r.ok) return null;
    return (await r.arrayBuffer()).byteLength;
  } catch { return null; }
}

export async function probeImageryZoom(lat: number, lng: number, from = 19, to = 12): Promise<number | null> {
  const placeholder = await tileBytes(lat, lng, 23);
  if (placeholder == null) return null;
  for (let z = from; z >= to; z--) {
    const n = await tileBytes(lat, lng, z);
    if (n == null) return null;
    // the placeholder is one fixed file; anything else is a photo
    if (Math.abs(n - placeholder) > 64) return z;
  }
  return to;
}
